import { normalizeBaseId } from '@/app/lib/courseIdFormatter';

export type AssistantEntityKind = 'course' | 'degree' | 'bus';

export type AssistantEntityMention = {
  kind: AssistantEntityKind;
  match: string;
  start: number;
  end: number;
  href: string;
  eyebrow: string;
  title: string;
  description: string;
  metadata?: string;
};

const NAMED_BUS_ROUTES = ['Weekend 1', 'Weekend 2', 'All Campuses', 'New Brunsquick', 'REXB', 'REXL', 'EE', 'LX'] as const;
const LETTER_BUS_ROUTES = ['A', 'B', 'C', 'F', 'H'] as const;
const DEGREE_STOPWORDS = new Set(['this', 'that', 'the', 'your', 'our', 'their', 'each', 'every', 'one', 'new', 'current', 'selected', 'completed', 'planned', 'remaining']);

function courseHref(code: string): string {
  return `/courses/${encodeURIComponent(normalizeBaseId(code))}`;
}

function degreeHref(name: string): string {
  return `/explore?view=degrees&q=${encodeURIComponent(name)}`;
}

function busHref(): string {
  return '/bus-tracker';
}

function courseMention(match: string, start: number, end: number): AssistantEntityMention {
  const code = normalizeBaseId(match);
  return {
    kind: 'course',
    match,
    start,
    end,
    href: courseHref(match),
    eyebrow: 'Course',
    title: code,
    description: 'Open the canonical course record for sections, prerequisites, and source-backed details.',
    metadata: 'Rutgers catalog identity',
  };
}

function degreeMention(match: string, start: number, end: number): AssistantEntityMention {
  const name = match.replace(/\s+/g, ' ').trim();
  return {
    kind: 'degree',
    match,
    start,
    end,
    href: degreeHref(name),
    eyebrow: 'Degree program',
    title: name,
    description: 'Search Degree Explorer for this Rutgers program. Requirements and progress stay on that page.',
    metadata: 'Catalog search',
  };
}

function busMention(match: string, start: number, end: number, route: string): AssistantEntityMention {
  return {
    kind: 'bus',
    match,
    start,
    end,
    href: busHref(),
    eyebrow: 'Campus bus',
    title: `${route} Route`,
    description: 'Open Bus Tracker for live arrivals. This preview does not claim a current vehicle or ETA.',
    metadata: 'Typical campus route',
  };
}

function pushMention(mentions: AssistantEntityMention[], next: AssistantEntityMention) {
  if (mentions.some((existing) => next.start < existing.end && next.end > existing.start)) return;
  mentions.push(next);
}

function collectRegex(text: string, pattern: RegExp, toMention: (match: string, start: number, end: number) => AssistantEntityMention | null): AssistantEntityMention[] {
  const mentions: AssistantEntityMention[] = [];
  for (const found of text.matchAll(pattern)) {
    const match = found[0];
    const start = found.index ?? 0;
    const mention = toMention(match, start, start + match.length);
    if (mention) mentions.push(mention);
  }
  return mentions;
}

function namedBusMentions(text: string): AssistantEntityMention[] {
  const mentions: AssistantEntityMention[] = [];
  for (const route of NAMED_BUS_ROUTES) {
    const pattern = new RegExp(`\\b${route.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?:\\s+Route)?\\b`, 'gi');
    for (const found of text.matchAll(pattern)) {
      const match = found[0];
      const start = found.index ?? 0;
      mentions.push(busMention(match, start, start + match.length, route));
    }
  }
  return mentions;
}

function letterBusMentions(text: string): AssistantEntityMention[] {
  const mentions: AssistantEntityMention[] = [];
  const pattern = new RegExp(`(?:(?:the|take the)\\s+)?\\b(${LETTER_BUS_ROUTES.join('|')})(?:\\s+Route|\\s+bus|\\s+shuttle)\\b`, 'gi');
  for (const found of text.matchAll(pattern)) {
    const route = (found[1] ?? found[0]).toUpperCase();
    const match = found[0];
    const start = found.index ?? 0;
    mentions.push(busMention(match, start, start + match.length, route));
  }
  return mentions;
}

function degreeNameIsCredible(name: string): boolean {
  const tokens = name.replace(/\s+(Major|Minor|Certificate)$/i, '').split(/\s+/).filter(Boolean);
  if (!tokens.length) return false;
  if (tokens.length === 1 && DEGREE_STOPWORDS.has(tokens[0].toLowerCase())) return false;
  return true;
}

function trimDegreeMatch(match: string, start: number): { match: string; start: number; end: number } | null {
  let remaining = match;
  let offset = start;
  while (true) {
    const token = remaining.match(/^([A-Za-z]+)\s+/);
    if (!token || !DEGREE_STOPWORDS.has(token[1].toLowerCase())) break;
    offset += token[0].length;
    remaining = remaining.slice(token[0].length);
  }
  if (!degreeNameIsCredible(remaining)) return null;
  return { match: remaining, start: offset, end: offset + remaining.length };
}

export function findAssistantEntityMentions(text: string): AssistantEntityMention[] {
  if (!text.trim()) return [];
  const candidates = [
    ...collectRegex(text, /\b\d{2}:\d{3}:\d{3}\b/g, (match, start, end) => courseMention(match, start, end)),
    ...collectRegex(text, /\b\d{3}:\d{3}\b/g, (match, start, end) => courseMention(match, start, end)),
    ...namedBusMentions(text),
    ...letterBusMentions(text),
    ...collectRegex(text, /\bBachelor of (?:Science|Arts|Fine Arts) in [A-Z][A-Za-z0-9 &'/-]{2,80}/g, (match, start) => {
      const trimmed = trimDegreeMatch(match, start);
      return trimmed ? degreeMention(trimmed.match, trimmed.start, trimmed.end) : null;
    }),
    ...collectRegex(text, /\b(?:B\.S\.|B\.A\.|BS|BA) in [A-Z][A-Za-z0-9 &'/-]{2,80}/g, (match, start) => {
      const trimmed = trimDegreeMatch(match, start);
      return trimmed ? degreeMention(trimmed.match, trimmed.start, trimmed.end) : null;
    }),
    ...collectRegex(text, /\b[A-Z][A-Za-z0-9][A-Za-z0-9 &'/-]{0,60} (?:Major|Minor|Certificate)\b/g, (match, start) => {
      const trimmed = trimDegreeMatch(match, start);
      return trimmed ? degreeMention(trimmed.match, trimmed.start, trimmed.end) : null;
    }),
  ];
  candidates.sort((left, right) => right.match.length - left.match.length || left.start - right.start);
  const selected: AssistantEntityMention[] = [];
  for (const candidate of candidates) pushMention(selected, candidate);
  return selected.sort((left, right) => left.start - right.start);
}

export function isSafeAssistantHref(href: string | undefined): string | null {
  if (!href) return null;
  const trimmed = href.trim();
  if (!trimmed || trimmed.startsWith('//')) return null;
  if (trimmed.startsWith('/')) {
    if (!/^\/(courses|explore|bus-tracker|scheduling|degree-planning|degree-search|map|dining|events|parking|planning|professors|rooms|research-opportunities)(\/|\?|$)/.test(trimmed)) return null;
    return trimmed;
  }
  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function mentionFromInternalHref(href: string, label: string): AssistantEntityMention | null {
  try {
    const url = new URL(href, 'https://scarletsync.app');
    if (url.pathname.startsWith('/courses/')) {
      const code = decodeURIComponent(url.pathname.slice('/courses/'.length));
      return courseMention(label || code, 0, (label || code).length);
    }
    if (url.searchParams.get('view') === 'degrees' || url.pathname.startsWith('/degree-search') || url.pathname.startsWith('/degree-planning')) {
      const query = url.searchParams.get('q') || url.searchParams.get('degree') || label;
      return degreeMention(query, 0, query.length);
    }
    if (url.pathname.startsWith('/bus-tracker')) {
      return busMention(label || 'Bus Tracker', 0, (label || 'Bus Tracker').length, label.replace(/\s+Route$/i, '') || 'Campus');
    }
  } catch {
    return null;
  }
  return null;
}
